import { useMemo } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  BookOpen,
  ClipboardList,
  BarChart3,
  Play,
  ChevronRight,
  Loader2,
  Clock,
  Target,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useExam } from "@/hooks/use-exams";
import { cleanExamText } from "@/lib/exam-display";
import { createErrorMessage } from "@/lib/error-message";
import { useSummary } from "@/hooks/use-analytics";
import { useTestHistory } from "@/hooks/use-tests";
import type { TestSession } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_main/exams/$slug/")({
  component: ExamDashboardPage,
});

export function ExamDashboardPage() {
  const params = useParams({ strict: false }) as { slug: string };
  const examSlug = params.slug;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const examQuery = useExam(examSlug);
  const summaryQuery = useSummary(examSlug);
  const historyQuery = useTestHistory(examSlug);

  const exam = examQuery.data;
  const summary = summaryQuery.data;
  const recentTests = useMemo<TestSession[]>(
    () => (historyQuery.data ?? []).slice(0, 5),
    [historyQuery.data],
  );

  const loading =
    examQuery.isLoading || summaryQuery.isLoading || historyQuery.isLoading;
  const error = examQuery.error;

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale === "en" ? "en-US" : "ko-KR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function getAccuracy(test: TestSession): number {
    if (test.total_questions === 0) return 0;
    return Math.round((test.correct_count / test.total_questions) * 100);
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {t("common.error")}: {createErrorMessage(error)}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-3xl">
                {cleanExamText(exam?.name) || t("common.exam")}
              </h1>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                {cleanExamText(exam?.description)}
              </p>
              {exam && (
                <Badge variant="secondary" className="mt-2">
                  {exam.question_count}{t("common.questions")}
                </Badge>
              )}
            </div>

            {/* Summary Stats */}
            {summary && (
              <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                          {t("exam.total_tests")}
                        </p>
                        <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-2xl">
                          {summary.total_tests}{t("common.times_suffix")}
                        </p>
                      </div>
                      <div className="rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                        <ClipboardList className="h-5 w-5 text-blue-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                          {t("exam.questions_solved")}
                        </p>
                        <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-2xl">
                          {summary.total_answered}{t("common.questions")}
                        </p>
                      </div>
                      <div className="rounded-lg bg-emerald-50 p-2 dark:bg-emerald-950">
                        <BookOpen className="h-5 w-5 text-emerald-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                          {t("exam.accuracy_rate")}
                        </p>
                        <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-2xl">
                          {Math.round(summary.accuracy_rate)}%
                        </p>
                      </div>
                      <div className="rounded-lg bg-amber-50 p-2 dark:bg-amber-950">
                        <Target className="h-5 w-5 text-amber-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                          {t("exam.avg_time")}
                        </p>
                        <p className="mt-1 text-xl font-bold text-zinc-900 dark:text-zinc-50 sm:text-2xl">
                          {summary.avg_time_ms > 0
                            ? `${Math.round(summary.avg_time_ms / 1000)}${t("common.seconds_suffix")}`
                            : "-"}
                        </p>
                      </div>
                      <div className="rounded-lg bg-purple-50 p-2 dark:bg-purple-950">
                        <Clock className="h-5 w-5 text-purple-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Quick Actions */}
            <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Card
                className="cursor-pointer transition-all hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700"
                onClick={() =>
                  navigate({
                    to: "/exams/$slug/test/new",
                    params: { slug: examSlug },
                  })
                }
              >
                <CardContent className="flex items-center gap-3 py-5">
                  <div className="rounded-lg bg-blue-50 p-2.5 dark:bg-blue-950">
                    <Play className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                      {t("exam.start_new")}
                    </p>
                    <p className="text-xs text-zinc-500">{t("exam.start_new_desc")}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-300" />
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer transition-all hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700"
                onClick={() =>
                  navigate({
                    to: "/exams/$slug/questions",
                    params: { slug: examSlug },
                  })
                }
              >
                <CardContent className="flex items-center gap-3 py-5">
                  <div className="rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-950">
                    <BookOpen className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                      {t("exam.question_bank")}
                    </p>
                    <p className="text-xs text-zinc-500">{t("exam.question_bank_desc")}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-300" />
                </CardContent>
              </Card>

              <Card
                className="cursor-pointer transition-all hover:shadow-md hover:border-amber-300 dark:hover:border-amber-700"
                onClick={() =>
                  navigate({
                    to: "/exams/$slug/analytics",
                    params: { slug: examSlug },
                  })
                }
              >
                <CardContent className="flex items-center gap-3 py-5">
                  <div className="rounded-lg bg-amber-50 p-2.5 dark:bg-amber-950">
                    <BarChart3 className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                      {t("exam.analytics")}
                    </p>
                    <p className="text-xs text-zinc-500">{t("exam.analytics_desc")}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-zinc-300" />
                </CardContent>
              </Card>
            </div>

            {/* Recent Tests */}
            {recentTests.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      {t("exam.recent_history")}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={() =>
                        navigate({
                          to: "/exams/$slug/history",
                          params: { slug: examSlug },
                        })
                      }
                    >
                      {t("exam.view_all")}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                  <CardDescription>{t("exam.recent_5")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recentTests.map((test) => {
                      const accuracy = getAccuracy(test);
                      return (
                        <div
                          key={test.id}
                          className="flex cursor-pointer items-center justify-between rounded-lg border border-zinc-100 p-3 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-900"
                          onClick={() =>
                            test.completed_at
                              ? navigate({
                                  to: "/exams/$slug/test/$id/result",
                                  params: { slug: examSlug, id: test.id },
                                })
                              : navigate({
                                  to: "/exams/$slug/test/$id",
                                  params: { slug: examSlug, id: test.id },
                                })
                          }
                        >
                          <div className="flex items-center gap-3">
                            <Badge
                              variant={
                                test.mode === "practice"
                                  ? "secondary"
                                  : "outline"
                              }
                              className="text-xs"
                            >
                              {test.mode === "practice" ? t("common.practice") : t("common.exam")}
                            </Badge>
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">
                              {formatDate(test.started_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-zinc-500">
                              {test.correct_count}/{test.total_questions}
                            </span>
                            <Badge
                              variant={
                                accuracy >= 80
                                  ? "default"
                                  : accuracy >= 60
                                    ? "secondary"
                                    : "destructive"
                              }
                            >
                              {accuracy}%
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
